<script setup>
/**
 * One switch, its state in words, and the sentence that says what the state MEANS.
 *
 * Three of the four things an owner can change on the Setup page are booleans, and they
 * are the same card three times over — so it is one component, for the same reason
 * `PermissionCard` is one: three hand-written copies is three places for the busy state,
 * the failure message or the "On"/"Off" word to be forgotten in exactly one of them.
 *
 * IT ALWAYS RENDERS A SENTENCE, never only a grey toggle. The master switch is the whole
 * point of this page — every workspace starts with it off and, before this screen, there
 * was no way to turn it on that did not involve us — so "off" has to say what is off
 * rather than leaving the reader to infer it from a control position. `body` is the
 * ON_BODY/OFF_BODY pair the caller picks, and it is required, not optional.
 *
 * THE SENTENCE IS SLATE IN BOTH STATES, deliberately. Off is the DEFAULT state of every
 * workspace, so it is not a warning: a colour that fires when nothing is wrong is a
 * colour nobody reads when something is (README §4).
 *
 * THE SWITCH IS DISABLED WHILE ITS OWN WRITE IS IN FLIGHT and remounted when that write
 * fails. Chatwoot's `Switch` flips its own copy on click whether or not the server
 * agreed, so a failed PATCH leaves a control claiming a state that was never stored;
 * bumping `switchEpoch` is the same fix, for the same reason, as `PermissionCard`.
 */
import Switch from 'dashboard/components-next/switch/Switch.vue';

import { useWasloI18n } from '../../i18n.js';

defineProps({
  heading: { type: String, required: true },
  /** The switch's own label, read out to a screen reader and shown beside it. */
  label: { type: String, required: true },
  modelValue: { type: Boolean, required: true },
  /** What this state means, in a sentence. Never omitted — see above. */
  body: { type: String, required: true },
  /** True while THIS card's write is in flight. */
  isSaving: { type: Boolean, default: false },
  /** Bumped by the page when a write fails, to snap the control back. */
  switchEpoch: { type: Number, default: 0 },
  /** A message from the last failed write on this card. */
  saveError: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();
</script>

<template>
  <section
    class="flex flex-col gap-4 p-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
    data-waslo-outbound-switch
  >
    <!--
      `flex-wrap` + `min-w-0` on the text column, and `shrink-0` on the control group.
      README §1: the htmx header's right-hand group could not wrap, so a two-word name
      pushed the control off-screen at panel width. The heading here is a translated
      phrase of unknown length in two languages.
    -->
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div class="flex flex-col min-w-0 gap-1">
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">{{ heading }}</h3>
        <p
          class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
          data-waslo-outbound-switch-body
        >
          {{ body }}
        </p>
      </div>
      <div class="flex items-center gap-3 shrink-0">
        <span class="text-sm text-n-slate-11" data-waslo-outbound-switch-state>
          {{
            isSaving
              ? t('WASLO.OUTBOUND.COMMON.SAVING')
              : modelValue
                ? t('WASLO.OUTBOUND.COMMON.ON')
                : t('WASLO.OUTBOUND.COMMON.OFF')
          }}
        </span>
        <Switch
          :key="switchEpoch"
          :model-value="modelValue"
          :disabled="isSaving"
          :aria-label="label"
          data-waslo-outbound-switch-control
          @update:model-value="next => emit('update:modelValue', next)"
        />
      </div>
    </div>

    <p
      v-if="saveError"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-outbound-switch-error
    >
      {{ saveError }}
    </p>

    <!-- Everything a particular switch owns: the test numbers, the pause banner. -->
    <slot />
  </section>
</template>
